import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { Terminal, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { setToken, isAuthed } from "../../lib/auth";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [gateState, setGateState] = useState("checking"); // checking | denied | allowed
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    // Access code check via query string. Pattern: /admin?<code>  (no key=)
    const raw = loc.search.startsWith("?") ? loc.search.slice(1) : "";
    const code = raw.split("&")[0]; // tolerate extra params
    if (!code) {
      setGateState("denied");
      return;
    }
    api.post("/admin/access-verify", { code })
      .then((r) => setGateState(r.data.valid ? "allowed" : "denied"))
      .catch(() => setGateState("denied"));
  }, [loc.search]);

  if (isAuthed()) return <Navigate to="/admin" replace />;
  if (gateState === "denied") return <Navigate to="/" replace />;
  if (gateState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-sm text-[#A1A1AA]">$ verifying access<span className="cursor-blink text-[#00FF66]">_</span></div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setToken(r.data.token);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-6 bg-grid">
      <div className="surface p-8 w-full max-w-md relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md border border-[#00FF66]/40 bg-[#00FF66]/10 flex items-center justify-center">
            <Terminal className="w-4 h-4 text-[#00FF66]" />
          </div>
          <span className="font-heading font-bold text-lg">genuine<span className="text-[#00FF66]">.</span>software</span>
        </Link>
        <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// admin terminal</div>
        <h1 className="font-heading font-bold text-2xl mt-1">Sign in</h1>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">email</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-term" required />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">password</label>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-term" required />
          </div>
          <button data-testid="login-submit" disabled={loading} className="btn-primary w-full justify-center">
            <Lock className="w-4 h-4" /> {loading ? "signing in..." : "sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
